import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import JaLayout from '@/components/feature/JaLayout';
import useSEO from '@/hooks/useSEO';

// ─────────────────────────────────────────────
// Shared components
// ─────────────────────────────────────────────
function Breadcrumb({ current }: { current: string }) {
  return (
    <div className="flex items-center gap-2 mb-4 text-white/60 text-[0.8125rem]">
      <Link to="/ja" className="hover:text-white transition-colors cursor-pointer">ホーム</Link>
      <i className="ri-arrow-right-s-line" />
      <Link to="/ja/services" className="hover:text-white transition-colors cursor-pointer">OEM/ODMサービス</Link>
      <i className="ri-arrow-right-s-line" />
      <span className="text-white/90">{current}</span>
    </div>
  );
}

const SERVICE_SUBNAV_ITEMS = [
  { label: '人気処方', path: '/ja/services/hot-formulas' },
  { label: '天然原料', path: '/ja/services/natural-ingredients' },
  { label: '各種剤型', path: '/ja/services/dosage-forms' },
  { label: 'パッケージデザイン', path: '/ja/services/packaging' },
];

function ServiceSubNav({ current }: { current: string }) {
  return (
    <section className="py-10 bg-[#f7faf7] border-t border-gray-100">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem]">その他のサービスページ</h3>
          <Link to="/ja/services" className="inline-flex items-center gap-2 text-[#4CAF50] hover:text-[#43a047] text-[0.9375rem] font-semibold transition-colors cursor-pointer group">
            <i className="ri-arrow-left-line transition-transform duration-200 group-hover:-translate-x-0.5" />
            サービス一覧に戻る
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICE_SUBNAV_ITEMS.map((s) => (
            <Link key={s.path} to={s.path} className={`bg-white rounded-xl p-5 border transition-all duration-200 cursor-pointer block hover:-translate-y-0.5 ${current === s.path ? 'border-[#4CAF50] bg-green-50' : 'border-gray-100 hover:border-green-200'}`}>
              <p className={`font-bold text-[0.9375rem] ${current === s.path ? 'text-[#4CAF50]' : 'text-[#1a2e1a]'}`}>{s.label}</p>
              {current === s.path && <p className="text-[#4CAF50] text-[0.75rem] mt-1">現在のページ</p>}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
// Hot Formulas Page
// ─────────────────────────────────────────────
const formulas = [
  {
    id: 1, icon: 'ri-heart-line', title: '天然美肌', color: '#f48fb1',
    desc: 'コラーゲン・ヒアルロン酸・ビタミンCを中心とした天然成分で、内側から潤いとハリのある肌をキープします。',
    tags: ['コラーゲン', 'ヒアルロン酸', 'ビタミンC', '植物エキス'],
    dosages: ['粉末', 'ゼリースティック', 'ソフトジェル'],
    img: 'https://readdy.ai/api/search-image?query=natural%20beauty%20supplement%20collagen%20hyaluronic%20acid%20skincare%20product%20with%20rose%20petals%20white%20background%20elegant%20feminine%20packaging%20health%20beauty%20photography&width=400&height=300&seq=hf-beauty-01&orientation=landscape',
  },
  {
    id: 2, icon: 'ri-sparkling-line', title: '女性インティメイトケア', color: '#ce93d8',
    desc: '乳酸菌・クランベリーエキスを組み合わせ、女性のデリケートゾーンの健康的な環境を維持し、快適さと安心感を提供します。',
    tags: ['乳酸菌', 'クランベリー', '乳酸菌', '女性ケア'],
    dosages: ['カプセル', '粉末'],
    img: 'https://readdy.ai/api/search-image?query=feminine%20health%20supplement%20probiotic%20cranberry%20extract%20product%20soft%20pastel%20purple%20pink%20background%20elegant%20women%20health%20care%20packaging&width=400&height=300&seq=hf-fem-01&orientation=landscape',
  },
  {
    id: 3, icon: 'ri-men-line', title: '男性活力', color: '#90caf9',
    desc: '牡蠣エキス・マカ・亜鉛を厳選し、男性の活力と体力を内側からサポートします。',
    tags: ['牡蠣エキス', 'マカ', '亜鉛', '活力処方'],
    dosages: ['カプセル', '錠剤'],
    img: 'https://readdy.ai/api/search-image?query=mens%20health%20supplement%20maca%20oyster%20extract%20zinc%20vitality%20product%20dark%20masculine%20packaging%20on%20clean%20white%20background%20professional%20health%20photography&width=400&height=300&seq=hf-men-01&orientation=landscape',
  },
  {
    id: 4, icon: 'ri-leaf-line', title: '腸活・スッキリ', color: '#a5d6a7',
    desc: 'プロバイオティクス・食物繊維・酵素の複合処方で、腸内フローラのバランスを保ち、毎日を軽やかに過ごせるようサポートします。',
    tags: ['プロバイオティクス', '食物繊維', '消化酵素', '腸活'],
    dosages: ['粉末', 'カプセル', 'ゼリースティック'],
    img: 'https://readdy.ai/api/search-image?query=digestive%20health%20probiotic%20fiber%20supplement%20green%20fresh%20natural%20ingredients%20gut%20health%20product%20on%20light%20background%20clean%20minimal%20photography&width=400&height=300&seq=hf-digest-01&orientation=landscape',
  },
  {
    id: 5, icon: 'ri-eye-line', title: '目の健康', color: '#80deea',
    desc: 'ルテイン・ゼアキサンチン・ブルーベリーエキスで目の健康を総合的に守ります。長時間のデジタル機器使用が多い方に最適です。',
    tags: ['ルテイン', 'ゼアキサンチン', 'ブルーベリーエキス', 'アイケア'],
    dosages: ['ソフトジェル', '錠剤'],
    img: 'https://readdy.ai/api/search-image?query=eye%20health%20supplement%20lutein%20zeaxanthin%20blueberry%20extract%20product%20on%20light%20background%20healthy%20vision%20care%20product%20photography%20natural%20light&width=400&height=300&seq=hf-eye-01&orientation=landscape',
  },
  {
    id: 6, icon: 'ri-riding-line', title: 'スポーツパフォーマンス', color: '#ffe082',
    desc: 'BCAA・クレアチン・プロテインなどスポーツ栄養成分で、筋肉の回復と体力向上をサポートします。',
    tags: ['BCAA', 'クレアチン', 'プロテイン', 'スポーツ栄養'],
    dosages: ['粉末', 'カプセル', '錠剤'],
    img: 'https://readdy.ai/api/search-image?query=sports%20nutrition%20supplement%20BCAA%20protein%20powder%20workout%20fitness%20product%20on%20dark%20sporty%20background%2C%20health%20bodybuilding%20supplement%20professional%20photography&width=400&height=300&seq=hf-sport-01&orientation=landscape',
  },
  {
    id: 7, icon: 'ri-moon-line', title: '快眠サポート', color: '#b39ddb',
    desc: '天然のL-テアニン・GABA・バレリアンエキスで心身のリラクゼーションを促し、睡眠の質向上をサポートします。',
    tags: ['L-テアニン', 'GABA', 'バレリアン', '睡眠サポート'],
    dosages: ['カプセル', '粉末', 'ゼリースティック'],
    img: 'https://readdy.ai/api/search-image?query=sleep%20supplement%20GABA%20valerian%20L-theanine%20natural%20sleep%20aid%20product%20lavender%20purple%20background%20calm%20relaxation%20health%20photography&width=400&height=300&seq=hf-sleep-01&orientation=landscape',
  },
  {
    id: 8, icon: 'ri-run-line', title: '骨・関節サポート', color: '#bcaaa4',
    desc: 'カルシウム・マグネシウム・ビタミンD3・グルコサミンで骨と関節の健康を強化します。中高年の方に特に適した処方です。',
    tags: ['カルシウム・マグネシウム', 'ビタミンD3', 'グルコサミン', '骨・関節健康'],
    dosages: ['錠剤', 'カプセル'],
    img: 'https://readdy.ai/api/search-image?query=bone%20joint%20health%20supplement%20calcium%20magnesium%20vitamin%20D%20glucosamine%20product%20on%20white%20background%2C%20strong%20healthy%20bones%20concept%20photography&width=400&height=300&seq=hf-bone-01&orientation=landscape',
  },
  {
    id: 9, icon: 'ri-seedling-line', title: '美髪ケア', color: '#8d6e63',
    desc: 'ビオチン・何首烏エキス・黒ごまで根本から髪の健康をサポートします。',
    tags: ['ビオチン', '何首烏', '黒ごま', '髪の健康'],
    dosages: ['カプセル', '粉末', 'ゼリースティック'],
    img: 'https://readdy.ai/api/search-image?query=hair%20health%20supplement%20biotin%20black%20sesame%20he%20shou%20wu%20product%20with%20natural%20herbs%20on%20clean%20white%20background%20elegant%20hair%20care%20product%20photography&width=400&height=300&seq=hf-hair-01&orientation=landscape',
  },
  {
    id: 10, icon: 'ri-emotion-happy-line', title: 'こどもの成長', color: '#ffcc80',
    desc: 'カルシウム・ビタミンD・DHA・プロバイオティクスで子どもの総合的な発育をサポート。安全で美味しく、子ども向けに設計されています。',
    tags: ['カルシウム', 'DHA', 'プロバイオティクス', '子ども安全'],
    dosages: ['粉末', 'チュアブル錠', 'ゼリースティック'],
    img: 'https://readdy.ai/api/search-image?query=children%20health%20supplement%20calcium%20DHA%20probiotic%20colorful%20fruit%20flavored%20vitamins%20for%20kids%20on%20bright%20cheerful%20background%2C%20safe%20natural%20ingredients&width=400&height=300&seq=hf-kids-01&orientation=landscape',
  },
  {
    id: 11, icon: 'ri-heart-pulse-line', title: 'ベビー処方', color: '#f8bbd0',
    desc: '最も穏やかな天然原料を使用した乳幼児専用処方。腸の健康と免疫力の発達をサポートします。',
    tags: ['乳幼児用プロバイオティクス', '初乳', 'ビタミン', '低刺激処方'],
    dosages: ['粉末', 'ドロップ'],
    img: 'https://readdy.ai/api/search-image?query=baby%20infant%20health%20supplement%20probiotic%20powder%20gentle%20natural%20formula%20for%20newborn%20toddler%20on%20soft%20pastel%20pink%20white%20background%20gentle%20photography&width=400&height=300&seq=hf-baby-01&orientation=landscape',
  },
  {
    id: 12, icon: 'ri-scales-3-line', title: 'ウェイト管理', color: '#c8e6c9',
    desc: 'ガルシニアカンボジア・白インゲン豆・グリーンコーヒーエキスで体重管理をサポート。健康的な生活習慣との組み合わせで効果的です。',
    tags: ['ガルシニアカンボジア', '白インゲン豆', 'グリーンコーヒー', '体重管理'],
    dosages: ['カプセル', '粉末'],
    img: 'https://readdy.ai/api/search-image?query=weight%20management%20slim%20supplement%20green%20coffee%20white%20kidney%20bean%20garcinia%20cambogia%20product%20on%20clean%20fresh%20green%20white%20background%20healthy%20slimming%20photography&width=400&height=300&seq=hf-slim-01&orientation=landscape',
  },
  {
    id: 13, icon: 'ri-sun-line', title: 'デイリー栄養補給', color: '#fff59d',
    desc: '現代人の食生活不足を補う総合ビタミン・ミネラルで、毎日の基礎的な健康ニーズをサポートします。',
    tags: ['マルチビタミン', 'ミネラル', '抗酸化', 'デイリーケア'],
    dosages: ['錠剤', 'カプセル', '粉末'],
    img: 'https://readdy.ai/api/search-image?query=multivitamin%20daily%20nutrition%20supplement%20colorful%20capsules%20tablets%20minerals%20vitamins%20on%20white%20background%20fresh%20healthy%20lifestyle%20concept%20photography&width=400&height=300&seq=hf-daily-01&orientation=landscape',
  },
  {
    id: 14, icon: 'ri-bear-smile-line', title: 'ペット向け', color: '#bcaaa4',
    desc: '犬・猫向けに設計されたペット健康処方。飼料添加物規制に準拠し、大切なペットの健康を守ります。',
    tags: ['ペット用プロバイオティクス', 'オメガ3', '関節ケア', '飼料規制'],
    dosages: ['粉末', 'おやつ剤型'],
    img: 'https://readdy.ai/api/search-image?query=pet%20health%20supplement%20dog%20cat%20natural%20organic%20probiotic%20omega3%20joint%20care%20product%20with%20cute%20pet%20imagery%20on%20light%20background%20natural%20health%20photography&width=400&height=300&seq=hf-pet-01&orientation=landscape',
  },
  {
    id: 15, icon: 'ri-mental-health-line', title: '集中・リラックス', color: '#b2dfdb',
    desc: 'ホスファチジルセリン・DHA・ビタミンB群で集中力と感情の安定をサポート。多忙なビジネスパーソンに最適な処方です。',
    tags: ['ホスファチジルセリン', 'DHA', 'ビタミンB群', 'メンタルサポート'],
    dosages: ['ソフトジェル', 'カプセル'],
    img: 'https://readdy.ai/api/search-image?query=brain%20health%20focus%20supplement%20phosphatidylserine%20DHA%20B%20vitamins%20cognitive%20function%20product%20on%20calm%20blue%20green%20background%20mental%20wellness%20photography&width=400&height=300&seq=hf-brain-01&orientation=landscape',
  },
  {
    id: 16, icon: 'ri-shield-star-line', title: '免疫強化', color: '#a5d6a7',
    desc: 'ビタミンC・亜鉛・エルダーベリーエキス・ラクトフェリンで免疫システムを総合的に強化します。',
    tags: ['ビタミンC', '亜鉛', 'エルダーベリー', '免疫強化'],
    dosages: ['錠剤', '粉末', 'カプセル'],
    img: 'https://readdy.ai/api/search-image?query=immune%20health%20supplement%20vitamin%20C%20zinc%20elderberry%20lactoferrin%20shield%20protection%20product%20on%20fresh%20green%20white%20background%20healthy%20immunity%20photography&width=400&height=300&seq=hf-immune-01&orientation=landscape',
  },
  {
    id: 17, icon: 'ri-bug-2-line', title: '善玉菌パワー', color: '#ffe0b2',
    desc: '多菌株プロバイオティクスとプレバイオティクスで腸内フローラを総合的にサポートし、健全な消化機能を維持します。',
    tags: ['多菌株プロバイオティクス', 'プレバイオティクス', '乳酸菌', '腸内フローラ'],
    dosages: ['粉末', 'カプセル', 'ゼリースティック'],
    img: 'https://readdy.ai/api/search-image?query=probiotic%20supplement%20multiple%20strains%20lactobacillus%20product%20with%20clean%20fresh%20natural%20background%20white%20green%20gut%20microbiome%20health%20photography&width=400&height=300&seq=hf-probiotic-01&orientation=landscape',
  },
];

function HotFormulasPage() {
  const [expanded, setExpanded] = useState<number | null>(null);
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=diverse%20collection%20of%20colorful%20health%20supplement%20products%20capsules%20powders%20gummies%20arranged%20beautifully%20on%20white%20marble%20surface%2C%20vibrant%20natural%20ingredients%20visible%2C%20professional%20product%20display%20photography&width=1920&height=420&seq=hf-hero-bg-01&orientation=landscape"
            alt="人気処方"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/65 to-[#1a3a1a]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb current="人気処方" />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">OEM/ODMサービス</span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">人気処方</h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              異なるターゲット層・生活スタイル・市場トレンドに合わせた処方方向をご提案。ブランドに最適な処方の方向性を見つけてください。
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white border-b border-gray-100 py-5">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { num: '17+', label: '処方方向' },
              { num: '3,200+', label: 'OEM実績品目' },
              { num: '30+', label: '対応剤型' },
              { num: '600+', label: '取引ブランド数' },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-[#4CAF50] font-bold text-[1.75rem] md:text-[2rem] leading-none mb-1">{s.num}</p>
                <p className="text-gray-500 text-[0.875rem]">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formula grid */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">処方一覧</span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">17の処方方向</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">カードをクリックすると詳細が展開されます。ブランドのポジショニングに最適な処方方向を見つけてください</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {formulas.map((f) => (
              <div
                key={f.id}
                className={`bg-white rounded-2xl overflow-hidden border transition-all duration-300 cursor-pointer ${expanded === f.id ? 'border-[#4CAF50]/50' : 'border-gray-100 hover:border-green-200 hover:-translate-y-1'}`}
                onClick={() => setExpanded(expanded === f.id ? null : f.id)}
              >
                <div className="w-full h-[240px] overflow-hidden relative">
                  <img src={f.img} alt={f.title} className="w-full h-full object-cover object-top transition-transform duration-300 hover:scale-105" />
                  <div className="absolute top-3 right-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ backgroundColor: `${f.color}30` }}>
                      <i className={`${f.icon} text-sm`} style={{ color: f.color }} />
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem]">{f.title}</h3>
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full transition-all duration-200 flex-shrink-0 ${expanded === f.id ? 'bg-[#4CAF50] text-white' : 'bg-green-50 text-[#4CAF50]'}`}>
                      <i className={`text-xs ${expanded === f.id ? 'ri-subtract-line' : 'ri-add-line'}`} />
                    </div>
                  </div>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-3 line-clamp-2">{f.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {f.dosages.map((d) => (
                      <span key={d} className="bg-green-50 border border-green-100 text-[#4CAF50] text-[0.75rem] px-2.5 py-1 rounded-full font-medium">{d}</span>
                    ))}
                  </div>
                </div>
                {expanded === f.id && (
                  <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                    <p className="text-gray-600 text-[0.875rem] leading-[1.75] mb-3">{f.desc}</p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {f.tags.map((tag) => (
                        <span key={tag} className="bg-[#f7faf7] border border-gray-200 text-gray-500 text-[0.75rem] px-2.5 py-1 rounded-full">{tag}</span>
                      ))}
                    </div>
                    <Link to="/ja/contact/form" onClick={(e) => e.stopPropagation()} className="whitespace-nowrap inline-flex items-center gap-1.5 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.8125rem] px-4 py-2 rounded-full transition-all duration-200 cursor-pointer">
                      この処方について問い合わせる
                      <i className="ri-arrow-right-line text-xs" />
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">ご希望の処方方向が見つかりませんか？</h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">康永生技は、ブランドのポジショニングとターゲット層に合わせた完全オリジナルの処方方向をご提案できます。研究開発チームまでお気軽にご相談ください。</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              処方開発についてご相談
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/ja/services/dosage-forms" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              各種剤型を見る
            </Link>
          </div>
        </div>
      </section>
      <ServiceSubNav current="/ja/services/hot-formulas" />
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-5">関連ページ</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: '天然原料', desc: '動物・植物・キノコ・藻類など、多彩な原料カテゴリをご紹介します。', href: '/ja/services/natural-ingredients' },
              { title: '各種剤型', desc: 'カプセル・錠剤・粉末・ゼリースティック…ブランドに最適な剤型をお選びください。', href: '/ja/services/dosage-forms' },
              { title: 'お問い合わせ', desc: 'お問い合わせフォームにご記入ください。康永がブランド構築をお手伝いします。', href: '/ja/contact/form' },
            ].map((r) => (
              <Link key={r.href} to={r.href} className="block bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 transition-all duration-200 cursor-pointer group">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5 group-hover:text-[#4CAF50] transition-colors">{r.title}</p>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{r.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─────────────────────────────────────────────
// Natural Ingredients Page
// ─────────────────────────────────────────────
const categories = [
  {
    id: 'animal', icon: 'ri-anchor-line', title: '動物由来', color: '#ef9a9a',
    intro: '海洋・陸上動物由来の高い生体利用率の原料。必須アミノ酸・タンパク質・活性成分を豊富に含み、吸収効率に優れています。',
    highlights: ['高い生体利用率', '必須アミノ酸豊富', '天然活性成分'],
    ingredients: [
      { name: '魚コラーゲン', desc: '魚の皮・鱗から抽出。低分子量で吸収が早く、美容処方のコア原料として活躍します。' },
      { name: 'フィッシュオイル（オメガ3）', desc: 'EPA・DHA豊富で心血管・脳の健康をサポート。高濃縮TG型で吸収性に優れています。' },
      { name: '牡蠣エキス', desc: '天然の亜鉛とアミノ酸の優れた供給源。男性健康処方に多用されるコア成分です。' },
      { name: 'ホエイプロテイン', desc: '素早く吸収される完全タンパク質。スポーツリカバリーと筋肉維持の主力原料です。' },
      { name: '初乳', desc: '免疫グロブリンと成長因子を豊富に含み、乳幼児・成人の免疫システムをサポートします。' },
    ],
    img: 'https://readdy.ai/api/search-image?query=animal%20derived%20natural%20supplement%20ingredients%20fish%20collagen%20marine%20omega3%20oyster%20extract%20on%20clean%20white%20background%2C%20professional%20health%20ingredient%20photography%20natural%20light&width=600&height=380&seq=ni-animal-01&orientation=landscape',
  },
  {
    id: 'plant', icon: 'ri-plant-line', title: '植物由来', color: '#4CAF50',
    intro: '世界中の根・茎・葉・果実のエキスを網羅し、多様な機能的サポートを提供。天然成分を好む現代の消費者ニーズに応えます。',
    highlights: ['天然植物エキス', '多様な機能', '消費者志向'],
    ingredients: [
      { name: 'クルクミン（ウコン）', desc: 'ウコン根の活性成分。強力な抗酸化特性を持ち、抗炎症処方の人気原料です。' },
      { name: 'クランベリーエキス', desc: 'プロアントシアニジン（PACs）豊富。女性のデリケートケアを代表する植物原料です。' },
      { name: 'ルテイン', desc: 'マリーゴールドの花びらから抽出。アイケア処方に最も重要なコア成分です。' },
      { name: 'エルダーベリーエキス', desc: 'アントシアニン豊富で免疫サポート効果あり。近年世界的に急成長している原料です。' },
      { name: 'マカエキス', desc: 'ペルーの高山植物。男性の活力と体力の天然サポート成分です。' },
      { name: 'ガルシニアカンボジア', desc: 'HCA（ヒドロキシクエン酸）を含む。体重管理処方によく使われる植物原料です。' },
    ],
    img: 'https://readdy.ai/api/search-image?query=plant%20botanical%20extract%20natural%20ingredients%20turmeric%20ginger%20herbs%20green%20tea%20extract%20on%20white%20wooden%20surface%2C%20health%20supplement%20raw%20materials%2C%20clean%20minimal%20photography&width=600&height=380&seq=ni-plant-01&orientation=landscape',
  },
  {
    id: 'mushroom', icon: 'ri-seedling-line', title: 'キノコ類', color: '#8d6e63',
    intro: '機能性キノコ類は多糖体・トリテルペンなどユニークな活性成分を含みます。東洋の伝統的健康法で長年使用され、現代科学でも効果が検証されています。',
    highlights: ['多糖体豊富', '東洋の伝統的英知', '多機能'],
    ingredients: [
      { name: '霊芝エキス', desc: 'トリテルペンと多糖体が豊富。免疫調節・抗酸化処方のプレミアムキノコ原料です。' },
      { name: '牛樟芝（台湾固有種）', desc: '台湾特有の希少キノコ。トリテルペン含量豊富で高付加価値処方に最適な素材です。' },
      { name: 'マイタケエキス', desc: 'D-フラクション多糖体。血糖管理と免疫サポート処方に多用される原料です。' },
      { name: '冬虫夏草エキス', desc: '貴重な伝統健康素材。プレミアムブランドの抗疲労処方に適しています。' },
    ],
    img: 'https://readdy.ai/api/search-image?query=functional%20mushroom%20supplement%20reishi%20ganoderma%20lion%20mane%20extract%20natural%20health%20product%20on%20dark%20wood%20background%2C%20traditional%20Chinese%20medicine%20herbs%20wellness%20photography&width=600&height=380&seq=ni-mushroom-01&orientation=landscape',
  },
  {
    id: 'algae', icon: 'ri-water-flash-line', title: '藻類・オイル', color: '#26a69a',
    intro: '海洋藻類はDHA・EPAおよび各種微量元素の優れた天然供給源。ベジタリアンにも適し、サステナブルな環境配慮型原料です。',
    highlights: ['ビーガン対応', '海洋サステナブル', '高純度DHA'],
    ingredients: [
      { name: '藻油DHA', desc: 'マイクロアルジェから抽出した純植物性DHA。ベジタリアンや乳幼児処方に最適です。' },
      { name: 'スピルリナ', desc: '超高タンパク質の藍藻。複数のビタミン・ミネラルを含み、優れた植物性タンパク源です。' },
      { name: 'クロレラ', desc: 'クロロフィルとビタミンB12が豊富。デトックスや日常補充に人気の原料です。' },
      { name: 'アスタキサンチン', desc: 'ヘマトコッカス藻から抽出。強力な抗酸化力を持つアイケア・美容処方の高機能原料です。' },
    ],
    img: 'https://readdy.ai/api/search-image?query=algae%20oil%20DHA%20spirulina%20chlorella%20astaxanthin%20marine%20plant%20based%20supplement%20ingredient%20on%20teal%20blue%20green%20background%2C%20sustainable%20ocean%20health%20product%20photography&width=600&height=380&seq=ni-algae-01&orientation=landscape',
  },
  {
    id: 'biochem', icon: 'ri-flask-line', title: '生化学系', color: '#5c6bc0',
    intro: '生化学技術で製造された高純度活性物質。明確な有効性・安定した含量を持ち、機能性サプリメント処方に欠かせないコア成分です。',
    highlights: ['高純度', '安定含量', '明確な有効性'],
    ingredients: [
      { name: 'コエンザイムQ10（CoQ10）', desc: 'ミトコンドリアのエネルギー代謝に関わる重要な補酵素。心血管・抗疲労処方のコア成分です。' },
      { name: 'GABA（γ-アミノ酪酸）', desc: '天然の神経伝達物質。睡眠・リラクゼーション処方の主要有効成分です。' },
      { name: 'NMN（ニコチンアミドモノヌクレオチド）', desc: 'NAD+の前駆体。高級アンチエイジング処方の人気プレミアム成分です。' },
      { name: 'L-テアニン', desc: 'お茶に含まれる天然アミノ酸。眠気を起こさずリラクゼーションを促進し、カフェインとの組み合わせで集中力を高めます。' },
      { name: 'グルコサミン＆コンドロイチン', desc: '関節軟骨修復のゴールデンコンビ。骨・関節健康処方に欠かせない成分の組み合わせです。' },
    ],
    img: 'https://readdy.ai/api/search-image?query=biochemical%20supplement%20CoQ10%20NMN%20GABA%20active%20compound%20laboratory%20research%20high%20purity%20extract%20on%20clean%20scientific%20minimal%20background%2C%20health%20biotech%20ingredient%20photography&width=600&height=380&seq=ni-biochem-01&orientation=landscape',
  },
  {
    id: 'other', icon: 'ri-star-line', title: 'その他の栄養素', color: '#ff9800',
    intro: 'ビタミン・ミネラル・プロバイオティクス・食物繊維は健康食品の基礎構成要素。康永は規格に適合した高品質原料を厳選し、バッチごとの含量安定性を保証します。',
    highlights: ['基礎必須', '含量安定', '多様な選択肢'],
    ingredients: [
      { name: 'ビタミンC・D3・E・Bコンプレックス', desc: '水溶性・脂溶性ビタミンシリーズ。マルチビタミン処方の必須基礎成分です。' },
      { name: 'カルシウム・マグネシウム・亜鉛・鉄', desc: '処方ニーズに応じた有機・無機塩形態のミネラルシリーズ。最適な吸収率を確保します。' },
      { name: '多菌株プロバイオティクス', desc: '乳酸菌・ビフィズス菌などの多菌株組み合わせ。有効性の目標に応じて菌株構成を柔軟に選択できます。' },
      { name: 'チコリ繊維（FOS/イヌリン）', desc: '天然プレバイオティクス。腸内善玉菌のエサとなり、プロバイオティクスとの組み合わせで相乗効果を発揮します。' },
      { name: '消化酵素複合物', desc: 'プロテアーゼ・アミラーゼ・リパーゼの組み合わせ。消化吸収をサポートし、腸活処方に多用されます。' },
    ],
    img: 'https://readdy.ai/api/search-image?query=vitamins%20minerals%20probiotic%20dietary%20fiber%20supplement%20ingredients%20colorful%20assortment%20on%20clean%20white%20background%2C%20essential%20nutrition%20daily%20supplement%20product%20photography&width=600&height=380&seq=ni-other-01&orientation=landscape',
  },
];

function NaturalIngredientsPage() {
  const [activeId, setActiveId] = useState<string | null>(null);
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=diverse%20natural%20raw%20ingredients%20herbs%20botanical%20extracts%20fish%20oil%20mushrooms%20algae%20vitamins%20arranged%20on%20clean%20white%20marble%20surface%2C%20health%20supplement%20raw%20material%20sourcing%20professional%20photography&width=1920&height=420&seq=ni-hero-bg-01&orientation=landscape"
            alt="天然原料"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/65 to-[#1a3a1a]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb current="天然原料" />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">OEM/ODMサービス</span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">天然原料</h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              動物・植物・キノコ・藻類から生化学栄養素まで、製品のポジショニングに合った原料方向をお選びください。
            </p>
          </div>
        </div>
      </section>

      {/* Category filter */}
      <section className="py-6 bg-white border-b border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setActiveId(null)} className={`whitespace-nowrap px-4 py-2 rounded-full text-[0.875rem] font-medium transition-all duration-200 cursor-pointer ${activeId === null ? 'bg-[#4CAF50] text-white' : 'bg-[#f7faf7] text-gray-600 border border-gray-200 hover:border-green-300 hover:text-[#4CAF50]'}`}>すべてのカテゴリ</button>
            {categories.map((cat) => (
              <button key={cat.id} onClick={() => setActiveId(cat.id === activeId ? null : cat.id)} className={`whitespace-nowrap px-4 py-2 rounded-full text-[0.875rem] font-medium transition-all duration-200 cursor-pointer ${activeId === cat.id ? 'bg-[#4CAF50] text-white' : 'bg-[#f7faf7] text-gray-600 border border-gray-200 hover:border-green-300 hover:text-[#4CAF50]'}`}>{cat.title}</button>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 space-y-8">
          {categories.filter((c) => activeId === null || c.id === activeId).map((cat) => (
            <div key={cat.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="flex flex-col lg:flex-row">
                <div className="w-full lg:w-[360px] flex-shrink-0">
                  <img src={cat.img} alt={cat.title} className="w-full h-[220px] lg:h-full object-cover object-top" />
                </div>
                <div className="flex-1 p-7">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${cat.color}20` }}>
                      <i className={`${cat.icon} text-xl`} style={{ color: cat.color }} />
                    </div>
                    <h2 className="text-[#1a2e1a] font-bold text-[1.375rem]">{cat.title}</h2>
                  </div>
                  <p className="text-gray-600 text-[1rem] leading-[1.75] mb-4">{cat.intro}</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {cat.highlights.map((h) => (
                      <span key={h} className="text-[0.8125rem] font-medium px-3 py-1 rounded-full border" style={{ color: cat.color, borderColor: `${cat.color}40`, backgroundColor: `${cat.color}10` }}>{h}</span>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {cat.ingredients.map((ing) => (
                      <div key={ing.name} className="flex items-start gap-3 bg-[#f7faf7] rounded-xl p-3.5">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-fill text-[#4CAF50] text-base" />
                        </div>
                        <div>
                          <p className="text-[#1a2e1a] font-semibold text-[0.875rem] mb-0.5">{ing.name}</p>
                          <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{ing.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quality assurance */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">原料品質管理</span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] mb-3">全ロット、厳格な入庫検査</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto">康永生技はすべての原料に対して厳格な入庫検査制度を実施しています</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { icon: 'ri-test-tube-line', title: '農薬残留検査', desc: '各入庫ロットで農薬残留多項目スクリーニング実施' },
              { icon: 'ri-drop-line', title: '重金属分析', desc: '鉛・水銀・ヒ素・カドミウムなど重金属の全面検査' },
              { icon: 'ri-bug-line', title: '微生物検査', desc: '生菌数・大腸菌など微生物安全性の確認' },
              { icon: 'ri-file-check-line', title: '有効成分含量確認', desc: '有効成分含量が規格書基準に適合しているかを確認' },
            ].map((item) => (
              <div key={item.title} className="bg-[#f7faf7] rounded-2xl p-5 text-center border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl mx-auto mb-3 border border-green-100">
                  <i className={`${item.icon} text-[#4CAF50] text-xl`} />
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5">{item.title}</h3>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">特定原料のコンプライアンスと価格をお知りになりたい方へ</h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">康永生技は原料の実現可能性評価と、台湾規制に準拠した原料選定アドバイスをご提供します。</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              原料プランニングについてご相談
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/ja/services/hot-formulas" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              人気処方を見る
            </Link>
          </div>
        </div>
      </section>
      <ServiceSubNav current="/ja/services/natural-ingredients" />
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-5">関連ページ</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: '人気処方', desc: '市場実績のある17の処方方向をご紹介。ブランドのベストスタートを見つけてください。', href: '/ja/services/hot-formulas' },
              { title: '各種剤型', desc: 'カプセル・錠剤・粉末・ゼリースティック…原料の特性を最大限に活かす剤型をお選びください。', href: '/ja/services/dosage-forms' },
              { title: 'ODM協力についてご相談', desc: 'お問い合わせフォームにご記入ください。康永が最適な原料の組み合わせをご提案します。', href: '/ja/contact/form' },
            ].map((r) => (
              <Link key={r.href} to={r.href} className="block bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 transition-all duration-200 cursor-pointer group">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5 group-hover:text-[#4CAF50] transition-colors">{r.title}</p>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{r.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─────────────────────────────────────────────
// Dosage Forms Page
// ─────────────────────────────────────────────
const dosageForms = [
  {
    id: 'powder', icon: 'ri-contrast-drop-line', title: '粉末', subtitle: '素早く溶ける・直感的な摂取', color: '#fff59d', colorDark: '#f9a825',
    desc: '粉末剤型は原料を細粉または顆粒状にし、水や食品に直接混ぜて摂取します。1日の摂取量が多い成分や素早い吸収が必要な処方に最適です。',
    process: '原料混合・粉砕 → 篩い分け・標準化 → 充填・分包（瓶またはスティック包） → 品質管理・密封',
    suitable: ['プロテイン補給', 'プロバイオティクス', 'ビタミン複合', '子ども栄養', 'スポーツ栄養'],
    advantages: ['摂取量の柔軟な調整が可能', 'フレーバー添加が容易', '大量補給に適合', '手軽に溶かして摂取'],
    note: '粉末は吸湿しやすいため、包装には特別な防湿設計が必要です。低温・低湿度環境での保存を推奨します。',
    img: 'https://readdy.ai/api/search-image?query=powder%20supplement%20scoop%20protein%20nutrition%20powder%20white%20powder%20in%20glass%20jar%20with%20spoon%20on%20clean%20white%20marble%20surface%2C%20health%20supplement%20product%20photography%20minimal&width=600&height=380&seq=df-powder-01&orientation=landscape',
  },
  {
    id: 'tablet', icon: 'ri-medicine-bottle-line', title: '錠剤', subtitle: '携帯に便利・正確な用量', color: '#c8e6c9', colorDark: '#2e7d32',
    desc: '錠剤は圧縮成型により外観が均一で用量が安定しており、市場で最も普及している健康食品の剤型です。複数の形状で製造可能で、コーティングにより外観と食感を改善できます。',
    process: '原料混合 → 造粒 → 圧縮成型 → 品質管理（硬度・崩壊度）→ コーティング（選択可）→ 分包',
    suitable: ['マルチビタミン', 'ミネラル', '骨・関節ケア', '抗酸化', 'デイリーサプリ'],
    advantages: ['高い生産効率・競争力のあるコスト', '携帯・保存が容易', '正確で安定した用量', 'コーティングで崩壊速度をコントロール可能'],
    note: '一部の原料は高温圧縮製法に適さないため、処方設計段階で原料の耐熱性確認が必要です。',
    img: 'https://readdy.ai/api/search-image?query=supplement%20tablets%20pills%20various%20colors%20shapes%20round%20oval%20on%20white%20background%2C%20pharmaceutical%20health%20product%20close%20up%20photography%2C%20clean%20minimal%20style&width=600&height=380&seq=df-tablet-01&orientation=landscape',
  },
  {
    id: 'capsule', icon: 'ri-capsule-line', title: 'カプセル', subtitle: '苦味マスク効果・高い消費者受容性', color: '#b2dfdb', colorDark: '#00695c',
    desc: 'カプセルはハードカプセル（粉末・顆粒充填）とソフトジェル（油性原料充填）に分かれます。市場での消費者受容性が最も高い剤型の一つで、原料の苦みや異臭を効果的にマスクできます。',
    process: '原料充填 → カプセル結合 → 研磨・仕分け → 品質管理（目視/機械検査）→ 分包',
    suitable: ['植物エキス', 'フィッシュオイル / 月見草油', 'プロバイオティクス', '美容処方', '男女健康'],
    advantages: ['原料の異味を効果的にマスク', '植物性または動物性カプセルから選択可', '見た目が良く識別しやすい', '油性原料に最適な剤型'],
    note: 'ソフトジェルの製造工程はハードカプセルより複雑でリードタイムがやや長めです。植物性カプセルはビーガン向け差別化セールスポイントになります。',
    img: 'https://readdy.ai/api/search-image?query=capsule%20supplement%20hard%20soft%20gel%20capsules%20green%20white%20transparent%20close%20up%20scattered%20on%20white%20background%2C%20health%20supplement%20pharmaceutical%20product%20photography&width=600&height=380&seq=df-capsule-01&orientation=landscape',
  },
  {
    id: 'jelly', icon: 'ri-drop-line', title: 'ゼリースティック', subtitle: '美味しい・幅広い消費者層に受け入れられる', color: '#f8bbd0', colorDark: '#ad1457',
    desc: 'ゼリースティックはゲル基材に有効成分を包み込み、噛んで食べるか絞り出して摂取します。水なしで摂取でき、子ども・高齢者・便利さを求める消費者に広く支持されています。',
    process: '溶液調製 → 有効成分添加 → モールドまたはパウチへの充填 → 冷却・成型 → 密封包装',
    suitable: ['コラーゲン', 'プロバイオティクス', '子どもビタミン', '睡眠処方', '美髪ケア'],
    advantages: ['水なしで摂取可能', '多彩なフレーバーオプション', '子ども・高齢者に最適', '高い製品差別化'],
    note: 'ゼリースティックは水分含量が高いため、粉末や錠剤に比べ賞味期限が短くなります。防腐・包装設計に特別な配慮が必要です。',
    img: 'https://readdy.ai/api/search-image?query=health%20supplement%20jelly%20stick%20gel%20collagen%20beauty%20product%20individual%20packet%20stick%20pouch%20on%20light%20background%2C%20fresh%20colorful%20fruit%20flavored%20health%20jelly%20product%20photography&width=600&height=380&seq=df-jelly-01&orientation=landscape',
  },
  {
    id: 'tea', icon: 'ri-cup-line', title: 'ティーバッグ', subtitle: '天然感・手軽に淹れて飲める', color: '#d7ccc8', colorDark: '#4e342e',
    desc: 'ティーバッグ剤型は乾燥植物素材または粉末をフィルター紙袋に充填し、天然植物の外観と香りを保ちます。「天然」「ハーブ」をブランドコンセプトにする製品に最適で、高い市場差別化を実現します。',
    process: '原料乾燥・粉砕 → 配合混合 → ティーバッグ充填 → ヒートシール → 外袋包装',
    suitable: ['ハーブ健康茶', 'スリミングティー', '睡眠サポートティー', 'デトックスケア', '女性バランス'],
    advantages: ['強い天然ハーブ感', '高い視覚的訴求力', 'ブランドストーリーマーケティングに適合', '原料の直感的な表現'],
    note: 'ティーバッグ剤型は粒度と充填均一性に特別な要件があります。お湯での有効成分の溶出率は事前評価が必要です。',
    img: 'https://readdy.ai/api/search-image?query=herbal%20wellness%20tea%20bag%20supplement%20dried%20flowers%20chamomile%20herbs%20botanical%20on%20white%20background%2C%20health%20tea%20product%20photography%20natural%20organic%20ingredients&width=600&height=380&seq=df-tea-01&orientation=landscape',
  },
  {
    id: 'liquid', icon: 'ri-water-percent-line', title: '液剤', subtitle: '素早い吸収・特定の方々に最適', color: '#b3e5fc', colorDark: '#0277bd',
    desc: '液剤には経口液・機能性飲料・ドロップなどの形式があります。液体に溶解した有効成分は吸収が早く、乳幼児・高齢者・嚥下困難な方に特に適しています。',
    process: '原料溶解・調製 → 殺菌処理 → 充填・密封（ボトル/アンプル）→ 品質管理・滅菌検証 → 出荷',
    suitable: ['乳幼児サプリ', 'シニア健康', '機能性飲料', 'コラーゲンドリンク', '素早い補給飲料'],
    advantages: ['最も速い吸収速度', '特定ニーズを持つ方々に適合', 'フレーバーカスタマイズ可能', '高い製品ビジュアル識別性'],
    note: '液剤製造は微生物管理要件が最も厳しく、充填環境が食品グレード基準を満たす必要があります。賞味期限の十分な検証も必要です。',
    img: 'https://readdy.ai/api/search-image?query=liquid%20health%20supplement%20drink%20bottle%20collagen%20beverage%20oral%20solution%20small%20bottle%20with%20natural%20fruit%20ingredients%20on%20clean%20white%20background%2C%20health%20drink%20product%20photography&width=600&height=380&seq=df-liquid-01&orientation=landscape',
  },
];

function DosageFormsPage() {
  const [openDetail, setOpenDetail] = useState<string | null>(null);
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=diverse%20health%20supplement%20dosage%20forms%20capsules%20tablets%20powder%20jelly%20drink%20arranged%20together%20on%20clean%20white%20surface%2C%20professional%20pharmaceutical%20product%20display%20photography%20natural%20light&width=1920&height=420&seq=df-hero-bg-01&orientation=landscape"
            alt="各種剤型"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/65 to-[#1a3a1a]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb current="各種剤型" />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">OEM/ODMサービス</span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">各種剤型</h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              異なる処方原料の特性と製品訴求に合わせて最適な剤型を選択することで、栄養成分を消費者がより取り入れやすくなります。
            </p>
          </div>
        </div>
      </section>

      {/* Overview pills */}
      <section className="bg-white border-b border-gray-100 py-5">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-gray-400 text-[0.875rem] mr-2">クイックジャンプ：</span>
            {dosageForms.map((d) => (
              <a key={d.id} href={`#df-${d.id}`} className="whitespace-nowrap flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[0.875rem] font-medium bg-[#f7faf7] text-gray-600 border border-gray-200 hover:border-green-300 hover:text-[#4CAF50] transition-all duration-200 cursor-pointer">
                <i className={`${d.icon} text-sm`} />
                {d.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Dosage forms */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 space-y-8">
          {dosageForms.map((d) => (
            <div id={`df-${d.id}`} key={d.id} className="scroll-mt-24 bg-white rounded-2xl overflow-hidden border border-gray-100">
              <div className="flex flex-col lg:flex-row">
                <div className="w-full lg:w-[320px] flex-shrink-0">
                  <img src={d.img} alt={d.title} className="w-full h-[220px] lg:h-full object-cover object-top" />
                </div>
                <div className="flex-1 p-7">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${d.colorDark}15` }}>
                        <i className={`${d.icon} text-xl`} style={{ color: d.colorDark }} />
                      </div>
                      <div>
                        <h2 className="text-[#1a2e1a] font-bold text-[1.375rem] leading-tight">{d.title}</h2>
                        <p style={{ color: d.colorDark }} className="text-[0.875rem] font-medium">{d.subtitle}</p>
                      </div>
                    </div>
                    <Link to="/ja/contact/form" className="whitespace-nowrap hidden sm:inline-flex items-center gap-1.5 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.8125rem] px-4 py-2 rounded-full transition-all duration-200 cursor-pointer flex-shrink-0">
                      問い合わせる
                    </Link>
                  </div>
                  <p className="text-gray-600 text-[0.9375rem] leading-[1.75] mb-4">{d.desc}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {d.suitable.map((s) => (
                      <span key={s} className="text-[0.75rem] font-medium px-2.5 py-1 rounded-full border" style={{ color: d.colorDark, borderColor: `${d.colorDark}30`, backgroundColor: `${d.colorDark}08` }}>{s}</span>
                    ))}
                  </div>
                  <button onClick={() => setOpenDetail(openDetail === d.id ? null : d.id)} className="flex items-center gap-2 text-[#4CAF50] font-medium text-[0.875rem] cursor-pointer hover:text-[#43a047] transition-colors duration-200">
                    <i className={`${openDetail === d.id ? 'ri-subtract-line' : 'ri-add-line'} text-base`} />
                    {openDetail === d.id ? '詳細を閉じる' : '製造工程と優位性を見る'}
                  </button>
                  {openDetail === d.id && (
                    <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-[#f7faf7] rounded-xl p-4">
                        <p className="text-[#1a2e1a] font-bold text-[0.875rem] mb-2 flex items-center gap-2">
                          <i className="ri-settings-3-line text-[#4CAF50]" />製造工程
                        </p>
                        <p className="text-gray-600 text-[0.8125rem] leading-[1.75]">{d.process}</p>
                      </div>
                      <div className="bg-[#f7faf7] rounded-xl p-4">
                        <p className="text-[#1a2e1a] font-bold text-[0.875rem] mb-2 flex items-center gap-2">
                          <i className="ri-star-line text-[#4CAF50]" />剤型の優位性
                        </p>
                        <ul className="space-y-1.5">
                          {d.advantages.map((a) => (
                            <li key={a} className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-[#4CAF50] text-sm flex-shrink-0 mt-0.5" />
                              <span className="text-gray-600 text-[0.8125rem] leading-[1.65]">{a}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="md:col-span-2 bg-amber-50 border border-amber-100 rounded-xl p-4">
                        <p className="text-[#1a2e1a] font-bold text-[0.875rem] mb-1.5 flex items-center gap-2">
                          <i className="ri-information-line text-amber-500" />注意事項
                        </p>
                        <p className="text-gray-600 text-[0.8125rem] leading-[1.75]">{d.note}</p>
                      </div>
                      <div className="md:col-span-2">
                        <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.9375rem] px-7 py-3 rounded-full transition-all duration-300 cursor-pointer">
                          この剤型について問い合わせる
                          <i className="ri-arrow-right-line" />
                        </Link>
                      </div>
                    </div>
                  )}
                  <div className="mt-4 sm:hidden">
                    <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-1.5 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.875rem] px-5 py-2.5 rounded-full transition-all duration-200 cursor-pointer">
                      問い合わせる
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">どの剤型が製品に最適かお分かりにならない方へ</h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">康永の研究開発チームが、処方特性・ターゲット消費者・市場ポジショニングに基づき、最適な剤型選択のプロフェッショナルなアドバイスをご提供します。</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              無料剤型相談
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/ja/services/hot-formulas" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              人気処方を見る
            </Link>
          </div>
        </div>
      </section>
      <ServiceSubNav current="/ja/services/dosage-forms" />
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-5">関連ページ</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: '人気処方', desc: '市場実績のある17の処方方向と最適な剤型の組み合わせをご紹介します。', href: '/ja/services/hot-formulas' },
              { title: 'パッケージデザイン', desc: '剤型が決まったら、ブランドイメージを最大限に表現するパッケージングをお選びください。', href: '/ja/services/packaging' },
              { title: 'お問い合わせ', desc: 'お問い合わせフォームにご記入ください。康永が最適な剤型選択をサポートします。', href: '/ja/contact/form' },
            ].map((r) => (
              <Link key={r.href} to={r.href} className="block bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 transition-all duration-200 cursor-pointer group">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5 group-hover:text-[#4CAF50] transition-colors">{r.title}</p>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{r.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─────────────────────────────────────────────
// Packaging Page
// ─────────────────────────────────────────────
const processSteps = [
  { num: '01', icon: 'ri-discuss-line', title: 'ニーズ確認', desc: 'ブランドスタイル・ターゲット層・流通チャネル要件とパッケージ仕様の方向性を確認します。' },
  { num: '02', icon: 'ri-palette-line', title: '初稿制作', desc: 'デザイナーがご要望に応じて2〜3セットのパッケージビジュアル草案をご提案します。' },
  { num: '03', icon: 'ri-printer-line', title: 'サンプル印刷', desc: 'デザイン確定後、印刷校正サンプルを制作し、実際の色と質感をご確認いただきます。' },
  { num: '04', icon: 'ri-edit-line', title: 'デザイン修正', desc: '校正結果に基づき、色差・フォント・レイアウトの細部を修正します。' },
  { num: '05', icon: 'ri-search-eye-line', title: '最終校正', desc: '法規ラベル内容の最終審査を行い、成分表示・表示フォーマットが規制に適合していることを確認します。' },
  { num: '06', icon: 'ri-send-plane-line', title: '印刷発注', desc: '最終稿確定後、印刷工場に発注し、スケジュール通りに量産・納品します。' },
];

const packagingTypes = [
  {
    id: 'box', icon: 'ri-archive-line', title: '各種箱型',
    desc: '外箱はブランドの第一印象を決めます。康永は天地蓋箱・マグネット蓋箱・引き出し箱など多様な箱型をご用意しており、製品ポジショニングと価格帯に合わせて最適な外箱デザインをお選びいただけます。',
    materials: '紙板（グレーボード/ホワイトボード/クラフト）、表面加工はマットフィルム/グロスフィルム/箔押し/エンボスから選択可',
    suitable: ['カプセル瓶外箱', 'ギフトセット組み合わせ', 'プレミアムブランド包装', '期間限定パッケージ'],
    img: 'https://readdy.ai/api/search-image?query=premium%20health%20supplement%20product%20box%20packaging%20cardboard%20white%20minimal%20clean%20design%20elegant%20brand%20packaging%20mockup%20on%20white%20background%20professional%20photography&width=600&height=380&seq=pkg-box-01&orientation=landscape',
  },
  {
    id: 'bottle', icon: 'ri-flask-line', title: 'ボトル・容器類',
    desc: 'ボトルはカプセル・錠剤・粉末製品の最も一般的な一次包材です。HDPE素材・PET透明ボトル・ガラス瓶・アルミ缶の中から選択でき、素材によって防湿・遮光・高級感に異なる優位性があります。',
    materials: 'HDPE素材ボトル（白/カラー）・PET透明ボトル・ガラス瓶・アルミ缶',
    suitable: ['カプセル/錠剤充填', 'プロテインパウダー', 'プレミアム処方製品', 'ビーガン認証製品'],
    img: 'https://readdy.ai/api/search-image?query=various%20supplement%20bottles%20HDPE%20PET%20glass%20amber%20brown%20white%20bottle%20types%20for%20capsules%20tablets%20powder%20on%20clean%20white%20background%2C%20pharmaceutical%20packaging%20product%20photography&width=600&height=380&seq=pkg-bottle-01&orientation=landscape',
  },
  {
    id: 'foil', icon: 'ri-file-paper-2-line', title: 'アルミ箔包装',
    desc: 'アルミ箔スタンドアップパウチまたはスティックパックは、粉末・顆粒・ゼリー類製品の主要な一次包材です。優れた防湿・遮光性を持ち、プロバイオティクスなど環境に敏感な原料に特に適しています。',
    materials: 'アルミ箔スタンドアップパウチ（チャック付き/なし）・アルミ箔スティックパック（スティックパック機充填）',
    suitable: ['粉末サプリ', 'プロバイオティクス処方', 'ゼリースティック', 'インスタントドリンク'],
    img: 'https://readdy.ai/api/search-image?query=aluminum%20foil%20pouch%20sachet%20packaging%20for%20health%20supplement%20powder%20probiotic%20on%20clean%20white%20background%2C%20metallic%20silver%20flexible%20packaging%20product%20photography&width=600&height=380&seq=pkg-foil-01&orientation=landscape',
  },
  {
    id: 'ptp', icon: 'ri-layout-grid-line', title: 'PTPブリスター包装',
    desc: 'PTP（Press Through Package）ブリスターパックは錠剤とカプセルのプレミアム包材選択肢です。1粒ずつ個別密封されており防湿性に優れ、高価格帯ブランドや高品質感を訴求する製品に適しています。日本・欧州市場製品によく見られます。',
    materials: 'PVC/PVDC底フィルム + アルミ箔シール、カスタム印刷カラーと仕様に対応',
    suitable: ['プレミアムブランドポジション', '日本スタイル高品質製品', '薬局チャンネル製品', '日韓輸出製品'],
    img: 'https://readdy.ai/api/search-image?query=PTP%20blister%20pack%20pharmaceutical%20supplement%20capsule%20tablet%20blister%20packaging%20silver%20aluminum%20press%20through%20package%20on%20white%20background%2C%20high%20end%20health%20product%20packaging%20photography&width=600&height=380&seq=pkg-ptp-01&orientation=landscape',
  },
];

function PackagingPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20health%20supplement%20product%20packaging%20design%20collection%20various%20boxes%20bottles%20pouches%20on%20clean%20white%20background%2C%20professional%20brand%20packaging%20display%20photography%20minimal&width=1920&height=420&seq=pkg-hero-bg-01&orientation=landscape"
            alt="パッケージデザイン"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a1a]/90 via-[#1a3a1a]/65 to-[#1a3a1a]/20" />
        </div>
        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
          <div className="max-w-2xl">
            <Breadcrumb current="パッケージデザイン" />
            <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">OEM/ODMサービス</span>
            <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">パッケージデザイン</h1>
            <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
              包材選定・ビジュアルデザインから法規表示まで、ブランドの完全で個性的な健康食品パッケージング構築をお手伝いします。
            </p>
          </div>
        </div>
      </section>

      {/* Design process */}
      <section className="py-20 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">デザインフロー</span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">パッケージデザイン6ステップ</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">ニーズ確認から印刷発注まで、各ステップを専門スタッフが管理し、完璧な最終仕上がりを保証します</p>
          </div>

          {/* Desktop */}
          <div className="hidden lg:grid grid-cols-6 gap-0 mb-10">
            {processSteps.map((s, idx) => (
              <div key={s.num} className="relative flex flex-col items-center">
                {idx < processSteps.length - 1 && (
                  <div className="absolute top-9 left-1/2 w-full h-0.5 bg-green-200 z-0" />
                )}
                <div className="relative z-10 flex flex-col items-center px-3 w-full">
                  <div className="w-[72px] h-[72px] flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white mb-4 relative">
                    <i className={`${s.icon} text-[#4CAF50] text-2xl`} />
                    <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-[#4CAF50] text-white text-[0.625rem] font-bold rounded-full">{s.num}</span>
                  </div>
                  <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5 text-center">{s.title}</h3>
                  <p className="text-gray-500 text-[0.8125rem] leading-[1.65] text-center">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile */}
          <div className="lg:hidden space-y-0">
            {processSteps.map((s, idx) => (
              <div key={s.num} className="flex gap-4">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white relative flex-shrink-0">
                    <i className={`${s.icon} text-[#4CAF50] text-xl`} />
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-[#4CAF50] text-white text-[0.625rem] font-bold rounded-full">{s.num}</span>
                  </div>
                  {idx < processSteps.length - 1 && (
                    <div className="w-0.5 flex-1 bg-green-200 my-2 min-h-[32px]" />
                  )}
                </div>
                <div className="pb-6 flex-1 min-w-0">
                  <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-1">{s.title}</h3>
                  <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-white rounded-2xl border border-green-100 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-xl flex-shrink-0 border border-green-100">
              <i className="ri-information-line text-[#4CAF50] text-xl" />
            </div>
            <div>
              <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">法規ラベル審査は省略できません</p>
              <p className="text-gray-500 text-[0.875rem] leading-[1.75]">パッケージ完成前に、康永は成分表示・重量表示・保存方法などの法定表示項目を審査し、台湾食品安全衛生管理法の規定に適合した状態で上市できるよう確認します。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Packaging types */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">包材タイプ</span>
            <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">多様な包材選択肢</h2>
            <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">製品ポジショニング・剤型特性・ターゲット市場に応じて、最適な包材の組み合わせをお選びください</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            {packagingTypes.map((p) => (
              <div key={p.id} className="bg-[#f7faf7] rounded-2xl overflow-hidden border border-gray-100 hover:border-green-200 transition-all duration-300">
                <div className="w-full h-[240px] overflow-hidden">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-white rounded-xl border border-green-100">
                      <i className={`${p.icon} text-[#4CAF50] text-lg`} />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.125rem]">{p.title}</h3>
                  </div>
                  <p className="text-gray-600 text-[0.9375rem] leading-[1.75] mb-4">{p.desc}</p>
                  <div className="bg-white rounded-xl p-3.5 mb-4 border border-gray-100">
                    <p className="text-[#1a2e1a] font-semibold text-[0.8125rem] mb-1 flex items-center gap-1.5">
                      <i className="ri-tools-line text-[#4CAF50] text-sm" />よく使われる素材
                    </p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{p.materials}</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {p.suitable.map((s) => (
                      <span key={s} className="bg-green-50 border border-green-100 text-[#4CAF50] text-[0.75rem] px-2.5 py-1 rounded-full font-medium">{s}</span>
                    ))}
                  </div>
                  <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-1.5 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[0.875rem] px-5 py-2.5 rounded-full transition-all duration-200 cursor-pointer">
                    包材についてご相談
                    <i className="ri-arrow-right-line text-sm" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Label compliance */}
      <section className="py-16 bg-[#f7faf7]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-10 items-center">
            <div className="w-full lg:w-1/2">
              <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5 border border-green-200">ラベルコンプライアンス</span>
              <h2 className="text-[#1a2e1a] font-bold text-[1.625rem] leading-tight mb-5">
                パッケージは美観だけでなく、<br />ラベルのコンプライアンスも重要です
              </h2>
              <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">
                台湾の食品表示規制では、品名・成分・添加物・重量・賞味期限・保存方法・メーカー情報などの表示が明確に義務付けられています。不適切な表示は製品回収や罰則につながる可能性があります。
              </p>
              <ul className="space-y-3">
                {[
                  '品名・ブランド名・識別表示',
                  '成分表示（含量の多い順に記載）',
                  '正味重量/容量表示',
                  '賞味期限と保存方法',
                  'メーカー名・住所・電話番号',
                  '原産国表示',
                  '1日摂取量とカロリー表示（該当する場合）',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-[#4CAF50] text-lg" />
                    </div>
                    <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=health%20supplement%20product%20label%20design%20packaging%20close%20up%20nutrition%20facts%20ingredient%20list%20regulatory%20compliance%20label%20printing%20on%20clean%20white%20bottle%2C%20professional%20product%20label%20photography&width=720&height=460&seq=pkg-label-01&orientation=landscape"
                  alt="ラベルコンプライアンス"
                  className="w-full h-[320px] lg:h-[380px] object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">
            康永が最適なパッケージソリューション構築をお手伝いします
          </h2>
          <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
            包材選定からラベル審査まで、ワンストップのパッケージデザインサービスで製品を棚で際立たせます。
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/ja/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              パッケージソリューションについてご相談
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/ja/cooperation/process" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
              協力プロセスを見る
            </Link>
          </div>
        </div>
      </section>
      <ServiceSubNav current="/ja/services/packaging" />
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-5">関連ページ</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: '各種剤型', desc: '剤型が決まったら、ブランドイメージを最大限に表現するパッケージングの組み合わせをお選びください。', href: '/ja/services/dosage-forms' },
              { title: '法規リスク', desc: 'パッケージ表示の法規要件。康永がすべての細部のコンプライアンスを確認します。', href: '/ja/about/regulation' },
              { title: 'お問い合わせ', desc: 'お問い合わせフォームにご記入ください。康永が最適なパッケージデザインをお手伝いします。', href: '/ja/contact/form' },
            ].map((r) => (
              <Link key={r.href} to={r.href} className="block bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 transition-all duration-200 cursor-pointer group">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5 group-hover:text-[#4CAF50] transition-colors">{r.title}</p>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{r.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

// ─────────────────────────────────────────────
// Router
// ─────────────────────────────────────────────
export default function JaServicesSubPage() {
  const location = useLocation();
  const path = location.pathname;

  const seoDataMap: Record<string, { title: string; description: string; keywords: string }> = {
    '/ja/services/hot-formulas': {
      title: '人気処方｜17種類の健康食品処方方向｜康永生技',
      description: '康永生技は17種類以上の実績ある健康食品処方方向を提供：天然美肌、女性の健康、男性活力、腸活、目の健康、スポーツ、睡眠、骨、美髪、子ども、ベビー、体重管理、デイリー栄養、ペット、集中、免疫、善玉菌。',
      keywords: '人気処方,17種類処方,健康食品ODM,美容処方,スポーツ栄養,プロバイオティクス処方',
    },
    '/ja/services/natural-ingredients': {
      title: '天然原料｜動物・植物・キノコ・藻類・生化学原料｜康永生技',
      description: '康永生技の天然原料分類：動物由来（コラーゲン、フィッシュオイル）、植物由来（クルクミン、クランベリー）、キノコ類（霊芝）、藻類・オイル、生化学系（CoQ10、GABA）、その他栄養素。',
      keywords: '天然原料,健康食品原料,植物エキス,動物由来,キノコサプリ,藻類DHA,CoQ10',
    },
    '/ja/services/dosage-forms': {
      title: '各種剤型｜粉末・錠剤・カプセル・ゼリー・茶包・液剤｜康永生技',
      description: '康永生技の剤型：粉末（素早く溶ける）、錠剤（携帯便利）、カプセル（苦味マスク）、ゼリースティック、ティーバッグ、液剤（素早い吸収）— 6つの剤型でOEM/ODM製造対応。',
      keywords: '剤型,粉末サプリ,錠剤製造,カプセルOEM,ゼリースティック,ティーバッグ,液剤サプリ',
    },
    '/ja/services/packaging': {
      title: 'パッケージデザイン｜箱・ボトル・アルミ箔・PTP・表示適合｜康永生技',
      description: '康永生技のパッケージデザイン：6ステップのデザインフロー、各種箱型、ボトル・容器類、アルミ箔包装、PTPブリスター包装、および健康食品の法定表示審査を提供します。',
      keywords: 'パッケージデザイン,健康食品包装,箱ボトルアルミ箔,PTPブリスター,表示適合,包装OEM',
    },
  };

  const currentSeo = seoDataMap[path] || seoDataMap['/ja/services/hot-formulas'];
  useSEO({ ...currentSeo, lang: 'ja', canonicalPath: path });

  return (
    <JaLayout>
      {path === '/ja/services/hot-formulas' && <HotFormulasPage />}
      {path === '/ja/services/natural-ingredients' && <NaturalIngredientsPage />}
      {path === '/ja/services/dosage-forms' && <DosageFormsPage />}
      {path === '/ja/services/packaging' && <PackagingPage />}
    </JaLayout>
  );
}
